import React, { useState, useEffect } from 'react';
import Button from '../../components/button';
import CustomInput from '../../components/customInput/CustomInput';
import CustomPaginationTable from '../../components/table/CustomPaginationTable';
import EyeIcon from '../../static/image/eyeIcon.svg';
import EditIcon from '../../static/image/editIcon.svg';
import DeleteIcon from '../../static/image/deleteIcon.svg';
import CreateRole from '../createRole/CreateRole';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRoles, deleteRole, fetchRoleDetails } from '../../redux/roleSlice';
import { EDIT_MODEL_CREATE, EDIT_MODEL_VIEW, EDIT_MODEL_EDIT } from '../../utils/constant';
import CommonDeleteModal from '../../components/commonDeleteModal/CommonDeleteModal';
import { formatDatetime } from '../../utils/dateUtils';
import { API_GET_ACCOUNT_ROLES } from '../../utils/apis';
import { decodedJWTPayload } from '../../utils/commonUtil';

function ManageRole() {
  const [inputValue2, setInputValue2] = useState('');
  const [pagination, setPagination] = useState({
    page: 0,  // 0-based
    size: 10
  });
  const [showCreateRole, setShowCreateRole] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [editMode, setEditMode] = useState(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [permission, setPermission] = useState('');

  const dispatch = useDispatch();
  const { data: roles, loading, roleDetails } = useSelector((state) => state.roles);

  useEffect(() => {
    fetchRolesData();
    const token = localStorage.getItem('access_token');
    if (token) {
      const payload = decodedJWTPayload(token);
      const mnId = payload?.mn_id;

      if (mnId) {
        const match = mnId.match(/\|010200-([RWRX]+)\|/);
        if (match) {
          setPermission(match[1]);
        }
      }
    }
  }, [dispatch]);

  const fetchRolesData = (paginationParams = pagination, roleName = '') => {
    const { page, size } = paginationParams;
    let url = `${API_GET_ACCOUNT_ROLES}?page=${page}&size=${size}`;
    if (roleName) {
      url += `&roleName=${encodeURIComponent(roleName)}`;
    }
    dispatch(fetchRoles(url));
  };

  useEffect(() => {
    if (roleDetails) {
      setSelectedRole(roleDetails);
      setIsFetchingDetails(false);
    }
  }, [roleDetails]);

  const columns = [
    {
      name: 'Role Name',
      cell: (row) => <div>{row.roleName}</div>
    },
    {
      name: 'Status',
      cell: (row) => <div>{row.isActive ? 'Active' : 'In-Active'}</div>
    },
    {
      name: 'Created By',
      cell: (row) => <div>{row.createdBy}</div>
    },
    {
      name: 'Created Date/Time',
      cell: (row) => <div>{formatDatetime(row.createdDatetime)}</div>
    },
    {
      name: 'Updated By',
      cell: (row) => <div>{row.updatedBy}</div>
    },
    {
      name: 'Updated Date/Time',
      cell: (row) => <div>{formatDatetime(row.updatedDatetime)}</div>
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div style={{ display: 'flex', gap: '10px' }}>
          <img
            src={EyeIcon}
            alt="Eye"
            onClick={() => handleViewRole(row.roleId)}
            style={{ cursor: 'pointer' }}
          />
          <img
            src={EditIcon}
            alt="Edit"
            onClick={() => handleEditRole(row.roleId)}
            style={{
              opacity: permission.includes('W') ? 1 : 0.5,
              cursor: permission.includes('W') ? 'pointer' : 'default'
            }}
          />
          <img
            src={DeleteIcon}
            alt="Delete"
            onClick={() => handleDeleteClick(row)}
            style={{
              opacity: permission === 'RWX' ? 1 : 0.5,
              cursor: permission === 'RWX' ? 'pointer' : 'default',
            }}
          />
        </div>
      ),
    }
  ];

  const handlePageNumberChange = (page) => {
    const newPagination = { ...pagination, page: page - 1 }; // Convert to 0-based
    setPagination(newPagination);
    fetchRolesData(newPagination, inputValue2);
  };

  const handleRowsPerPageChange = (newPerPage, page) => {
    const newPagination = { page: page - 1, size: newPerPage }; // Convert to 0-based
    setPagination(newPagination);
    fetchRolesData(newPagination, inputValue2);
  };

  const openCreateRolePage = () => {
    if (permission.includes('W')) {
      setShowCreateRole(true);
      setEditMode(EDIT_MODEL_CREATE);
    }
  };

  const closeCreateRolePage = () => {
    setShowCreateRole(false);
    setEditMode(undefined);
  };

  const handleEditRole = async (roleId) => {
    if (permission.includes('W')) {
      try {
        setIsFetchingDetails(true);
        await dispatch(fetchRoleDetails(roleId));
        setEditMode(EDIT_MODEL_EDIT);
      } catch (error) {
        console.error('Failed to fetch role details:', error);
        setIsFetchingDetails(false);
      }
    }
  };

  const handleViewRole = async (roleId) => {
    try {
      setIsFetchingDetails(true);
      await dispatch(fetchRoleDetails(roleId));
      setEditMode(EDIT_MODEL_VIEW);
    } catch (error) {
      console.error('Failed to fetch role details:', error);
      setIsFetchingDetails(false);
    }
  };

  const refetchRoles = () => {
    fetchRolesData(pagination, inputValue2);
  };

  const handleDeleteClick = (role) => {
    if (permission === 'RWX') {
      setSelectedRole(role);
      setShowDeleteModal(true);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleConfirmDelete = () => {
    dispatch(deleteRole(selectedRole.roleId))
      .unwrap()
      .then(() => {
        refetchRoles();
        setShowDeleteModal(false);
      })
      .catch((error) => {
        console.error('Delete role failed:', error);
        setShowDeleteModal(false);
      });
  };

  const handleSearch = () => {
    const newPagination = { ...pagination, page: 0 }; // Reset to first page
    setPagination(newPagination);
    fetchRolesData(newPagination, inputValue2);
  };

  const handleReset = () => {
    setInputValue2('');
    const newPagination = { ...pagination, page: 0 }; // Reset to first page
    setPagination(newPagination);
    fetchRolesData(newPagination);
  };

  return (
    <>
      {!editMode && (
        <div className="manage-user-container">
          <div style={{ padding: '30px' }}>
            <div>Manage Role</div>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '30px', gap: '20px', flexWrap: 'wrap' }}>
              <Button
                className="manage-user-btn"
                onClick={openCreateRolePage}
                style={{
                  opacity: permission.includes('W') ? 1 : 0.5,
                  pointerEvents: permission.includes('W') ? 'auto' : 'none',
                }}
              >
                CREATE ROLE
              </Button>
              <div>
                <CustomInput
                  placeholder="Role Name"
                  style={{ width: '240px', height: '45px' }}
                  value={inputValue2}
                  onChange={(e) => setInputValue2(e.target.value)}
                />
              </div>
              <Button className="manage-user-btn" onClick={handleSearch}>
                SEARCH
              </Button>
              <Button className="manage-user-btn" onClick={handleReset}>
                RESET
              </Button>
            </div>
            <div style={{ marginTop: '20px' }}>
              <CustomPaginationTable
                data={roles.content || []}
                totalRows={roles.totalElements || 0}
                loading={loading}
                perPage={pagination.size}
                currentPage={pagination.page + 1} // Convert to 1-based for display
                onPageChange={handlePageNumberChange}
                columns={columns}
                onPerRowsChange={handleRowsPerPageChange}
                isHiddenPag={false}
              />
            </div>
          </div>
        </div>
      )}
      {editMode && (
        <CreateRole
          editMode={editMode}
          role={selectedRole}
          onCancel={closeCreateRolePage}
          onRoleCreated={refetchRoles}
        />
      )}
      <CommonDeleteModal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}

export default ManageRole;